import { DateTime } from '@ksyos/date-time';
import { Avatar, Badge, Card, Collection, CollectionLink, SectionHeader, Text } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { Fragment, useEffect, useState } from 'react';
import type { IEmploymentRoleWithProfilePicture } from '@common/interfaces/employmentRoleWithProfilePicture';
import { PractitionerRole } from '@common/enums/practitionerRole';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { OrganizationPayoutManagersLogs } from './OrganizationPayoutManagersLogs';

interface IOrganizationPayoutManagersProps {
    organizationId: string;
}

export function OrganizationPayoutManagers(props: Readonly<IOrganizationPayoutManagersProps>) {
    const navigate = useNavigate();
    const [employmentRoles, setEmploymentRoles] = useState<IEmploymentRoleWithProfilePicture[]>([]);

    useEffect(() => {
        loadOrganizationPayoutManagers();
    }, [props.organizationId]);

    return (
        <>
            <SectionHeader title={translateKey('organization.organizationPayoutManagers.title')} />
            <Card>
                {employmentRoles.length > 0 ? (
                    <Collection type="ul">
                        {employmentRoles.map((employmentRole) => (
                            <Fragment key={employmentRole.id}>
                                <CollectionLink
                                    mediaItem={
                                        <Avatar
                                            imageUrl={employmentRole.employment.practitioner.profilePictureUrl}
                                            initials={getDisplayInitials(employmentRole.employment.practitioner)}
                                            size={56}
                                        />
                                    }
                                    onClick={() => redirectToProfilePage(employmentRole)}
                                    subtitle={employmentRole.employment.practitioner.uid?.toString()}
                                    title={getDisplayName(
                                        employmentRole.employment.practitioner,
                                        false,
                                        'nameGivenNameFamily'
                                    )}
                                >
                                    {employmentRole.endDate && (
                                        <Badge
                                            text={`${translateKey(
                                                'employment.employmentRole.activeUntil'
                                            )} ${DateTime.from(employmentRole.endDate).format('DD-MM-YYYY')}`}
                                        />
                                    )}
                                </CollectionLink>
                            </Fragment>
                        ))}
                    </Collection>
                ) : (
                    <Card.Body>
                        <Text>
                            {translateKey('organization.organizationPayoutManagers.noOrganizationManagersMessage')}
                        </Text>
                    </Card.Body>
                )}
                <OrganizationPayoutManagersLogs organizationId={props.organizationId} />
            </Card>
        </>
    );

    async function loadOrganizationPayoutManagers() {
        const url = `/api/organizations/${props.organizationId}/employmentRoles?practitionerRole=${PractitionerRole.organizationPayoutManager}`;
        const result = await fetchJson(url, 'GET');
        setEmploymentRoles(result.json.employmentRoles);
    }

    function redirectToProfilePage(employmentRole: IEmploymentRoleWithProfilePicture) {
        navigate(`/employments/${employmentRole.employment.id}/personalSettings`);
    }
}
